import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { keyBy, uniq } from 'lodash';
import moment from 'moment';

import { Typography } from '@material-ui/core';
import { useTranslation, toast } from '@common/utils';
import { useSettings } from '@common/hooks';
import http from '@common/http';

function CheckoutSummary({ booking, pricing, VATLines = [] }) {
	const classes = useStyles();
	const tr = useTranslation();
	const { locale } = useSelector(state => state.locale);

	const { currencySymbol } = useSettings();

	const { totalBeds, totalRooms, totalServices, totalFees, totalMandatory, products, productsGrouped, totalUpgrades } = booking;

	const {
		total,
		totalVAT,
		discountWithCodeInfo: {
			appliedAmount,
			code,
		},
	} = pricing;

	const [productById, setProductById] = useState({});

	useEffect(() => {

		Promise
			.all(uniq(products, 'product').map(p => http.get(`/api/kiosks/coliving/products/${p.product}`).catch(() => ({}))))
			.then(responses => responses.map(response => response.data).filter(Boolean))
			.then(products => setProductById(keyBy(products, '_id')))
			.catch(error => {
				console.error(error);
				toast(error);
			});

	}, [productsGrouped]);

	return (
		<>
			<div className={classes.summary}>
				<Typography variant="h3">
					{tr('Booking details')}
				</Typography>

				{booking.productsGrouped.map((p, i) => {

					const product = productById[p.product];

					const productVAT = VATLines.find(v => v.products.includes(String(p.product)));

					if (!product) {
						return;
					}

					if (p.fee) {
						return;
					}

					if (p.mandatory) {
						return;
					}

					if (p.upgrade) {
						return;
					}

					const isDorm = p.productAssetType === 'dorm';

					const duration = moment(p.to).endOf('day').diff(moment(p.from).startOf('day'), 'days');

					const cityTaxProduct = booking.productsGrouped.find(p => p.fee);
					const cityTaxVAT = VATLines.find(v => v.products.includes(String(cityTaxProduct?.product)));
					const cityTaxPricePerProduct = cityTaxProduct && p.productIsCityTaxApplied ? cityTaxProduct.price * duration * p.productPersonCapacity : 0;
					const productMandatoryTotal = (p.mandatoryBookingProducts || []).reduce((accumulator, product) => {
						return accumulator + product.total;
					}, 0);

					return (
						<div key={`product-${i}`}>
							<div className={classes.product}>
								<Typography variant="h4" className={classes.headline}>
									{product?.i18n[locale]?.title || product?.title || '-'}
								</Typography>
								{p.from && p.to && <div className={classes.dates}>
									<div>
										<b>{tr('Check-in')}</b>
										&nbsp;
										{moment(p.from).format('MMM DD, YYYY')}
									</div>
									<div>
										<b>{tr('Check-Out')}</b>
										&nbsp;
										{moment(p.to).format('MMM DD, YYYY')}
									</div>
								</div>}
							</div>


							<div className={classes.rows}>
								<div className={classes.row} style={{ paddingBottom: '4px' }}>
									<div>{tr(isDorm ? 'Beds' : 'Rooms')}</div>
									<div>{productVAT?.percent ? (p.total / (1 + productVAT?.percent / 100)).toFixed(2) : p.total.toFixed(2)}{currencySymbol}</div>
								</div>

								{productVAT &&
									<div className={classes.row} style={{ paddingTop: 0 }}>
										<div>
											{`${tr('VAT')} (${productVAT?.percent || 0}%)`}
										</div>
										<div>
											{(p.total - (p.total / (1 + productVAT?.percent / 100))).toFixed(2)}{currencySymbol}
										</div>
									</div>
								}

								{cityTaxProduct && p.productIsCityTaxApplied &&
									<>
										<div className={classes.row} style={{ paddingBottom: '4px' }}>
											<div>{tr('City tax')}</div>
											<div>{(cityTaxPricePerProduct / (1 + cityTaxVAT?.percent / 100)).toFixed(2)}{currencySymbol}</div>
										</div>
										{cityTaxVAT &&
											<div className={classes.row} style={{ paddingTop: 0 }}>
												<div>
													{`${tr('VAT')} (${cityTaxVAT?.percent || 0}%)`}
												</div>
												<div>
													{(cityTaxPricePerProduct - (cityTaxPricePerProduct / (1 + cityTaxVAT?.percent / 100))).toFixed(2)}{currencySymbol}
												</div>
											</div>
										}
										<div className={classes.separator} />
									</>
								}

								{p.totalServices > 0 && <><div className={classes.separator} />
									<div className={classes.row}>
										<div>{tr('Services')}</div>
										<div>{p.totalServices}{currencySymbol}</div>
									</div>
								</>}

								{p.mandatoryBookingProducts?.length > 0 &&
									<>
										<div className={classes.row} style={{ paddingBottom: '4px' }}>
											<div>{tr('Extras')}</div>
										</div>

										{p.mandatoryBookingProducts.map(p => {
											const vatPercent = VATLines.find(v => v.products.includes(String(p.product)))?.percent;
											return (
												<div key={p._id} style={{ paddingBottom: '12px' }}>
													<div className={classes.subRow}>
														<div>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</div>
														<div>{(p.total - (p.total - (p.total / (1 + vatPercent / 100)))).toFixed(2)}{currencySymbol}/{productById[p.product]?.mandatoryType === 'recurring' ? tr('month') : tr('unit')}</div>
													</div>
													<div className={classes.subRow}>
														<div>
															&nbsp;&nbsp;&nbsp;&nbsp;{`${tr('VAT')} (${vatPercent || 0}%)`}
														</div>
														<div>
															{(p.total - (p.total / (1 + vatPercent / 100))).toFixed(2)}{currencySymbol}
														</div>
													</div>
												</div>
											);
										})}
									</>
								}

								<div className={classes.separator} />
								<div className={`${classes.row} ${classes.total}`}>
									<div>
										{tr('Total (taxes incl.)')}
									</div>
									<div>
										{p.total + productMandatoryTotal + (cityTaxProduct?.total || 0)}{currencySymbol}
										{/* {p.total + ((cityTaxPricePerProduct || 0) * (p.quantity || 1)) + (p.mandatoryBookingProducts.reduce((p, c) => p + c.total, 0))}{currencySymbol} */}
									</div>
								</div>
							</div>
						</div>
					);
				})}

				{booking.productsGrouped.filter(i => i.upgrade).length > 0 &&
					<>
						<div className={classes.separator} />
						<div className={classes.row} style={{ paddingBottom: '4px' }}>
							<div>{tr('Extras and upgrades')}</div>
						</div>

						{booking.productsGrouped.filter(i => i.upgrade).map(p => {
							const vatPercent = VATLines.find(v => v.products.includes(String(p.product)))?.percent;
							return (
								p.upgrade === true && !!productById[p.product] &&
								<div key={p._id} style={{ paddingBottom: '12px' }}>
									<div className={classes.subRow}>
										<div>&nbsp;&nbsp;&nbsp;&nbsp;{productById[p.product]?.i18n[locale]?.title || productById[p.product]?.title}</div>
										<div>{(p.total - (p.total - (p.total / (1 + vatPercent / 100)))).toFixed(2)}{currencySymbol}/{productById[p.product]?.upgradeType === 'recurring' ? tr('month') : tr('unit')}</div>
									</div>
									<div className={classes.subRow}>
										<div>
											&nbsp;&nbsp;&nbsp;&nbsp;{`${tr('VAT')} (${vatPercent || 0}%)`}
										</div>
										<div>
											{(p.total - (p.total / (1 + vatPercent / 100))).toFixed(2)}{currencySymbol}
										</div>
									</div>
								</div>
							);
						})
						}
						<div className={classes.separator} />
						<div className={`${classes.row} ${classes.total}`}>
							<div>
								{tr('Total (taxes incl.)')}
							</div>
							<div>
								{totalUpgrades}{currencySymbol}
							</div>
						</div>
					</>
				}
			</div>
			<div className={classes.summary}>
				<Typography variant="h3">
					{tr('Booking summary')}
				</Typography>

				<div className={classes.rows}>

					{totalRooms > 0 &&
						<>
							<div className={classes.row}>
								<div>{tr('Rooms')}</div>
								<div>{totalRooms}{currencySymbol}</div>
							</div>
							<div className={classes.separator} />
						</>
					}

					{totalBeds > 0 &&
						<>
							<div className={classes.row}>
								<div>{tr('Beds')}</div>
								<div>{totalBeds}{currencySymbol}</div>
							</div>
							<div className={classes.separator} />
						</>
					}

					{totalFees > 0 &&
						<>
							<div className={classes.row}>
								<div>{tr('City tax')}</div>
								<div>{totalFees}{currencySymbol}</div>
							</div>
							<div className={classes.separator} />
						</>
					}

					{(totalMandatory > 0 || totalUpgrades > 0) &&
						<>
							<div className={classes.row}>
								<div>{tr('Extras')}</div>
								<div>{(totalMandatory || 0) + (totalUpgrades || 0)}{currencySymbol}</div>
							</div>
							<div className={classes.separator} />
						</>
					}

					{totalServices > 0 && <div className={classes.row}>
						<div>{tr('Services')}</div>
						<div>{totalServices}{currencySymbol}</div>
					</div>}

					<div className={classes.separator} />

					{totalVAT >= 0 && VATLines.map(vat => <div key={vat.percent} className={`${classes.row}`}>
						<div>
							{`${tr('VAT')} (${vat.percent}%)`}
						</div>
						<div>
							{vat.amount.toFixed(2)}{currencySymbol}
						</div>
					</div>)}
					{code && appliedAmount >= 0 && <div className={`${classes.row} ${classes.total}`}>
						<div>
							{`${tr('Discount')} (${code})`}
						</div>
						<div>
							&minus;{appliedAmount}{currencySymbol}
						</div>
					</div>}
					{booking.totalUpgrades > 0 &&
						<>
							<div className={`${classes.row} ${classes.total}`}>
								<Typography variant="h5">
									{tr('Total paid')}
								</Typography>
								<Typography variant="h5">
									{booking.total - total}{currencySymbol}
								</Typography>
							</div>
							<div className={`${classes.row} ${classes.total}`}>
								<Typography variant="h5">
									{tr('Total due (taxes incl.)')}
								</Typography>
								<Typography variant="h5">
									{total}{currencySymbol}
								</Typography>
							</div>
						</>
					}
					{!booking.totalUpgrades < 0 || booking.totalUpgrades === 0 &&
						<div className={`${classes.row} ${classes.total}`}>
							<Typography variant="h5">
								{tr('Total (taxes incl.)')}
							</Typography>
							<Typography variant="h5">
								{total}{currencySymbol}
							</Typography>
						</div>
					}
				</div>
			</div>
		</>
	);
}

CheckoutSummary.propTypes = {
	booking: PropTypes.object.isRequired,
	pricing: PropTypes.object.isRequired,
	VATLines: PropTypes.array.isRequired,
};

export default CheckoutSummary;

const useStyles = makeStyles(theme => ({
	summary: {
		marginBottom: theme.spacing(5),
		padding: `${theme.spacing(4)}px ${theme.spacing(3)}px 0`,
		borderRadius: '4px',
		backgroundColor: theme.palette.custom.background,
	},
	rows: {
		padding: `${theme.spacing(1)}px 0 ${theme.spacing(2)}px`,
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		padding: `${theme.spacing(2)}px 0`,
	},
	subRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		fontSize: 'small',
	},
	rowQTY: {
		paddingTop: 0,
	},
	total: {
		fontWeight: 600,
	},
	separator: {
		height: '1px',
		backgroundColor: theme.palette.custom.separator,
	},

	headline: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	product: {
		// marginBottom: theme.spacing(3),
	},

	section: {
		paddingLeft: 0,
		fontSize: '18px',
		color: theme.palette.primary.light,
		fontWeight: 600,
	},
	col: {
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
		borderBottom: 0,
	},
	first: {
		paddingLeft: 0,
	},
	last: {
		paddingRight: 0,
	},
	sep: {
		paddingLeft: 0,
		paddingRight: 0,
	},
	separator2: {
		height: '1px',
		backgroundColor: theme.palette.custom.separator,
	},

}));
